// One platform: /growth/platforms/:id
//
// The research behind it, with its sources as links, and the three things the owner
// can change. Each saves itself, and from then on a seed leaves it alone.

import { Link, useParams } from 'react-router-dom'
import { api } from '@/api'
import { isApiError } from '@/api/client'
import type { Platform, PlatformWrite } from '@/api/growthPlatforms'
import { BUCKETS, VS_SCORELM, type Bucket, type VsScoreLm } from '@/api/types'
import { PageHeader } from '@/components/shell/PageHeader'
import { Badge, ButtonLink, Card, CardHeader, EditableSelectField, EmptyState, ErrorState, KeyValueList, LoadingState, ToggleRow } from '@/components/ui'
import { BUCKET_LABELS, PLATFORM_KIND_LABELS, platformPrice, PUBLIC_PAGES_LABELS, VS_SCORELM_LABELS } from '@/lib/growth'
import { useDocumentTitle } from '@/lib/hooks'
import { invalidateQueries, setQueryData, useQuery } from '@/lib/query'
import { BaselineCard, BucketBadge, PriceVsBadge, VsBadge } from './platformShared'
import { SafeExternalLink } from './shared'

const BUCKET_OPTIONS = BUCKETS.map((b) => ({ value: b, label: BUCKET_LABELS[b] }))
const VS_OPTIONS = VS_SCORELM.map((v) => ({ value: v, label: VS_SCORELM_LABELS[v] }))

/** The research's notes, whatever shape they came in, as text. */
function FeatureNotes({ notes }: { notes: unknown }) {
  const shown = (v: unknown) => (typeof v === 'string' ? v : typeof v === 'boolean' ? (v ? 'Yes' : 'No') : JSON.stringify(v))
  if (typeof notes === 'string') return notes ? <p className="whitespace-pre-wrap break-words text-[13px] text-ink">{notes}</p> : null
  if (Array.isArray(notes)) {
    return notes.length ? (
      <ul className="grid gap-1 text-[13px] text-ink">
        {notes.map((n, i) => (
          <li key={i} className="break-words">{shown(n)}</li>
        ))}
      </ul>
    ) : null
  }
  if (!notes || typeof notes !== 'object') return null
  const entries = Object.entries(notes as Record<string, unknown>)
  return entries.length ? <KeyValueList items={entries.map(([k, v]) => ({ label: k.replace(/_/g, ' '), value: shown(v) }))} /> : null
}

function Sources({ platform: p }: { platform: Platform }) {
  if (p.sources.length === 0) return <p className="text-[13px] text-muted">No sources yet, so treat this row as unverified.</p>
  return (
    <ul aria-label="Sources" className="grid gap-3">
      {p.sources.map((s) => (
        <li key={s.url} className="grid gap-0.5 text-[13px]">
          <SafeExternalLink href={s.url} />
          {s.quote ? <p className="break-words text-muted">“{s.quote}”</p> : null}
          {s.date_checked ? <p className="text-xs text-subtle">Checked {s.date_checked}</p> : null}
        </li>
      ))}
    </ul>
  )
}

export default function PlatformDetailPage() {
  const { id: rawId } = useParams()
  const id = Number(rawId)
  const validId = Number.isInteger(id) && id > 0
  const { data: p, error, status, refetch } = useQuery(validId ? ['growth', 'platform', id] : null, (signal) => api.growth.platforms.get(id, signal))
  useDocumentTitle(p?.name ?? 'Platform')

  if (!validId || (status === 'error' && isApiError(error) && error.code === 'not_found')) {
    return (
      <Card>
        <EmptyState title="Platform not found" description={validId ? `There's no platform with id ${id}.` : `“${rawId}” isn't a platform id.`} action={<ButtonLink to="/growth/platforms">Back to platforms</ButtonLink>} />
      </Card>
    )
  }
  if (status === 'loading' && !p) return <LoadingState label="Loading platform…" />
  if (!p) {
    return (
      <Card>
        <ErrorState error={error} title="Couldn't load this platform" onRetry={refetch} />
      </Card>
    )
  }

  const save = async (patch: PlatformWrite) => {
    const { platform } = await api.growth.platforms.update(p.id, patch)
    setQueryData(['growth', 'platform', id], platform)
    invalidateQueries('growth')
  }
  const edited = (field: string) => (p.edited_by_owner.includes(field) ? 'Set by you, so a research update leaves it alone.' : undefined)

  return (
    <div className="grid grid-cols-1 gap-5">
      <PageHeader
        crumbs={[{ label: 'Platforms', to: '/growth/platforms' }, { label: p.name }]}
        title={p.name}
        subtitle={p.website ? <SafeExternalLink href={p.website} /> : undefined}
        badges={
          <>
            <BucketBadge bucket={p.bucket} />
            {p.verified ? null : <Badge tone="outline">Unverified</Badge>}
          </>
        }
      />

      <div className="grid items-start gap-4 lg:grid-cols-[1.2fr_1fr]">
        <div className="grid content-start gap-4">
          <Card>
            <CardHeader title="Against ScoreLM" />
            <div className="grid gap-4">
              <div className="flex flex-wrap items-center gap-2">
                <VsBadge value={p.vs_scorelm} />
                <PriceVsBadge value={p.price_vs_scorelm} />
              </div>
              {p.vs_reason ? <p className="whitespace-pre-wrap break-words text-[13.5px] text-ink">{p.vs_reason}</p> : null}
              <KeyValueList
                items={[
                  { label: 'Price', value: platformPrice(p) },
                  ...(p.price_model ? [{ label: 'Price model', value: p.price_model }] : []),
                  ...(p.target_customers ? [{ label: 'For', value: p.target_customers }] : []),
                  ...(p.sports.length ? [{ label: 'Sports', value: p.sports.join(', ') }] : []),
                  { label: 'Kind', value: PLATFORM_KIND_LABELS[p.kind] },
                ]}
              />
              <FeatureNotes notes={p.feature_notes} />
            </div>
          </Card>

          <Card>
            <CardHeader title="Sources" />
            <Sources platform={p} />
          </Card>

          <Card>
            <CardHeader title="How we spot it" />
            <KeyValueList
              items={[
                { label: 'Hosts', value: p.host_patterns.length ? <span className="font-mono text-xs">{p.host_patterns.join(', ')}</span> : '—' },
                { label: 'Footer', value: p.footer_fingerprints.length ? p.footer_fingerprints.join(', ') : '—' },
                { label: 'Public league pages', value: PUBLIC_PAGES_LABELS[p.public_league_pages] },
                ...(p.public_page_pattern ? [{ label: 'Page pattern', value: <span className="font-mono text-xs [overflow-wrap:anywhere]">{p.public_page_pattern}</span> }] : []),
                ...(p.public_page_example ? [{ label: 'Example', value: <SafeExternalLink href={p.public_page_example} /> }] : []),
              ]}
            />
          </Card>
        </div>

        <div className="grid content-start gap-4">
          <Card>
            <CardHeader title="Your call" description="What to do about leads on this platform. Emailing any bucket stays allowed." />
            <div className="grid gap-4">
              <EditableSelectField label="Bucket" value={p.bucket} options={BUCKET_OPTIONS} hint={edited('bucket')} onSave={(v) => save({ bucket: v as Bucket })} />
              <EditableSelectField label="vs ScoreLM" value={p.vs_scorelm} options={VS_OPTIONS} hint={edited('vs_scorelm')} onSave={(v) => save({ vs_scorelm: v as VsScoreLm })} />
              <ToggleRow label="Target" description={edited('is_target') ?? 'Worth going after.'} checked={p.is_target} onToggle={(is_target) => save({ is_target })} />
            </div>
          </Card>
          <Card>
            <CardHeader title="Leads" />
            <p className="text-[13.5px] text-ink">
              <Link to={`/growth/leads?platform_id=${p.id}`} className="font-semibold underline decoration-line-strong underline-offset-2 hover:text-accent">
                {p.lead_count} {p.lead_count === 1 ? 'lead' : 'leads'}
              </Link>{' '}
              on this platform.
            </p>
          </Card>
          <BaselineCard />
        </div>
      </div>
    </div>
  )
}
